// =============================================================================
// SimSense Platform — Auth Hook
// =============================================================================
// Convenience hook wrapping the Zustand auth store.

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuthStore } from "../state/authStore";

export type { AuthUser, UserTier } from "../state/authStore";

// Routes where we can skip auth check for first-time visitors (no prior session)
const GUEST_ROUTES = new Set(["/", "/login", "/signup", "/pricing"]);

export function useAuth() {
  const { user, loading, error, checkAuth, login, logout, register, loginWithGoogle } = useAuthStore();
  const location = useLocation();

  useEffect(() => {
    // Skip auth API calls on guest pages when the user has never logged in.
    // This prevents 401 noise in browser console for unauthenticated visitors.
    // Users who have logged in before will have the hint flag, so auth check runs.
    // OAuth callbacks include ?auth=1 to force auth check after redirect.
    const isGuestRoute = GUEST_ROUTES.has(location.pathname);
    const hasLoggedInBefore = localStorage.getItem("ss_auth_hint") === "1";
    const params = new URLSearchParams(location.search);
    const isOAuthReturn = params.get("auth") === "1";
    if (isGuestRoute && !hasLoggedInBefore && !isOAuthReturn && !useAuthStore.getState()._checked) {
      useAuthStore.setState({ loading: false, _checked: true });
      return;
    }
    checkAuth();
  }, [checkAuth, location.pathname, location.search]);

  return {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isIracingLinked: !!user?.iracingId,
    // Use dynamic capabilities from EntitlementBroker
    isPro: user?.capabilities?.has_pro || user?.plan === "pro" || user?.plan === "admin",
    isAdmin: user?.capabilities?.is_admin || user?.plan === "admin",
    login,
    logout,
    register,
    loginWithGoogle,
  };
}
